<template>
  <span class="logo" :class="'logo--' + tamanho" :style="estilo">
    <img v-if="logo && !falhouLogo" :src="logo" :alt="nome" @error="falhouLogo = true" />
    <span v-else-if="monograma" class="logo__monograma">{{ monograma }}</span>
    <CIcon v-else :name="icone || 'cil-puzzle'" />
  </span>
</template>

<script>
/*
 * Identidade visual de um conector ou provedor.
 *
 * Três níveis, do mais específico para o mais genérico:
 *
 * 1. `logo` — a marca, servida pela API. O endereço vem pronto no catálogo:
 *    a arte é versionada junto do conector, no repositório da API, para
 *    publicar um conector não exigir build deste painel.
 * 2. `monograma` — as iniciais do nome sobre a cor da marca. Serve para os
 *    serviços cujo logotipo não temos direito de distribuir no pacote: ainda
 *    identifica o serviço de relance, coisa que um ícone genérico não faz —
 *    sete integrações com o mesmo desenho de engrenagem não se distinguem.
 * 3. `icone` — o ícone temático do conector (telefone, caminhão, envelope),
 *    usado quando não há nem marca nem nome de provedor.
 */
export default {
  name: 'LogoIntegracao',
  props: {
    // Endereço da marca, como o catálogo o devolve.
    logo: {
      type: String,
      default: null,
    },
    // Nome do provedor ou do conector, base do monograma.
    nome: {
      type: String,
      default: '',
    },
    // Cor da marca, declarada no manifesto.
    cor: {
      type: String,
      default: null,
    },
    icone: {
      type: String,
      default: null,
    },
    // Monograma só faz sentido para o provedor (a marca); para o conector, o
    // ícone temático diz mais que as iniciais de "Cálculo e Rastreio de Frete".
    usarMonograma: {
      type: Boolean,
      default: false,
    },
    tamanho: {
      type: String,
      default: 'medio',
      validator: (valor) => ['pequeno', 'medio', 'grande'].includes(valor),
    },
  },
  data() {
    return {
      /*
       * Ter endereço não é ter imagem. O catálogo devolve o caminho declarado
       * mesmo quando a arte não está no acervo, e ele resolve contra a origem
       * do painel, que não guarda mais nenhuma — painel novo com API antiga
       * desenhava o ícone de imagem quebrada em vez de descer para o
       * monograma, que é a razão de a escada existir.
       */
      falhouLogo: false,
    };
  },
  watch: {
    // Conector diferente, marca diferente: a falha anterior não vale para ela.
    logo() {
      this.falhouLogo = false;
    },
  },
  computed: {
    monograma() {
      if (!this.usarMonograma || !this.nome) {
        return '';
      }

      const palavras = this.nome
        .replace(/\(.*?\)/g, '')
        .split(/[\s\-/]+/)
        .filter((palavra) => palavra.length > 1);

      if (!palavras.length) {
        return this.nome.slice(0, 2).toUpperCase();
      }

      // Duas iniciais para nome composto, duas primeiras letras para nome
      // único — "SimFrete" vira SI, "M&O Sistemas" vira MS.
      if (palavras.length === 1) {
        return palavras[0].slice(0, 2).toUpperCase();
      }

      return (palavras[0][0] + palavras[1][0]).toUpperCase();
    },

    estilo() {
      if (!this.cor || this.logo) {
        return {};
      }

      return {
        background: this.corDeFundo,
        color: this.cor,
      };
    },

    /*
     * Fundo derivado da própria cor da marca, em baixa opacidade: mantém o
     * contraste do texto e evita ter que declarar duas cores por provedor.
     */
    corDeFundo() {
      const canais = this.canaisDaCor;

      return canais ? `rgba(${canais.join(',')}, 0.12)` : null;
    },

    canaisDaCor() {
      const hexadecimal = /^#([0-9a-f]{6})$/i.exec(this.cor || '');

      if (!hexadecimal) {
        return null;
      }

      const valor = parseInt(hexadecimal[1], 16);

      return [(valor >> 16) & 255, (valor >> 8) & 255, valor & 255];
    },
  },
};
</script>

<style scoped>
.logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #eceaf3;
  color: #3a3e81;
  flex-shrink: 0;
  overflow: hidden;
}

/*
 * A maioria das marcas é um logotipo deitado (nome ao lado do símbolo). Limitar
 * a largura junto com a altura deixaria essas em um fiapo ilegível dentro de uma
 * caixa quadrada — aqui a largura aproveita quase toda a caixa e a altura é que
 * segura a proporção.
 */
.logo img {
  max-width: 88%;
  max-height: 74%;
  object-fit: contain;
}

.logo__monograma {
  font-weight: 700;
  letter-spacing: 0.5px;
  line-height: 1;
}

.logo--pequeno {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  font-size: 12px;
}

.logo--medio {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  font-size: 14px;
}

.logo--grande {
  width: 52px;
  height: 52px;
  font-size: 18px;
}
</style>
